import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Plus, 
  Search, 
  Building2, 
  MapPin, 
  Pencil, 
  Trash2,
  Home,
  Store,
  LandPlot,
  Filter,
  Layers,
  Users,
  Eye,
  UserPlus,
  UserMinus,
  FileText,
  Percent,
  XCircle,
  RefreshCw,
  FileCheck,
  Calendar,
  Image as ImageIcon,
  PenTool,
  Copy,
  Send,
  Mail as MailIcon
} from 'lucide-react';
import { PropertyGalleryModal } from '../components/PropertyGalleryModal';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogFooter 
} from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as CalendarComponent } from '../components/ui/calendar';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { cn } from '../lib/utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { propertiesAPI, clientsAPI, agencySettingsAPI } from '../lib/api';
import { SignaturePad } from '../components/SignaturePad';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { KeysInventoryTab } from '../components/keys/KeysInventoryTab';
import { KeyRound } from 'lucide-react';
import { 
  formatCurrency, 
  statusLabels, 
  getStatusColor, 
  propertyTypeLabels, 
  transactionTypeLabels,
  bamakoDistricts 
} from '../lib/utils';
import { toast } from 'sonner';
import ModuleTasksPanel from '../components/ModuleTasksPanel';

const propertyTypeIcons = {
  terrain: LandPlot,
  maison: Home,
  appartement: Building2,
  local_commercial: Store,
  immeuble: Layers,
};

const unitTypeLabels = {
  appartement: 'Appartement',
  boutique: 'Boutique',
  bureau: 'Bureau',
  studio: 'Studio',
};

const initialFormData = {
  title: '',
  property_type: 'maison',
  transaction_type: 'vente',
  price: '',
  currency: 'XOF',
  surface: '',
  bedrooms: '',
  bathrooms: '',
  address: '',
  district: '',
  city: 'Bamako',
  description: '',
  features: [],
  images: [],
  status: 'disponible',
  property_code: '',
  is_multi_unit: false,
  total_units: '',
  commission_rate: 5,
  owner_name: '',
  owner_phone: '',
  owner_email: '',
  owner_address: '',
  owner_city: '',
  owner_country: 'Mali',
  owner_id_type: '',
  owner_id_number: '',
  owner_birth_date: '',
  owner_birth_place: '',
  owner_nationality: '',
  owner_profession: '',
  owner_marital_status: '',
  mandate_type: 'exclusif',
  mandate_duration_months: 12,
  mandate_security_deposit: '',
  mandate_advance_months: '',
  mandate_file_fee: '',
  mandate_entry_commission_rate: '',
  mandate_monthly_management_fee_rate: '',
  mandate_vat_rate: '',
  mandate_notes: '',
  mandate_start_date: new Date(),
};

const initialUnitData = {
  unit_name: '',
  unit_type: 'appartement',
  rent_amount: '',
  currency: 'XOF',
  surface: '',
  rooms: '',
  description: '',
  commission_rate: '',
};

const Properties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get('focus');

  // Arrivée depuis un message cliquable : filtrer sur le bien ciblé
  useEffect(() => {
    if (!focusId || !properties.length) return;
    const target = properties.find((p) => p.id === focusId);
    if (target) setSearchQuery(target.property_code || target.title || '');
  }, [focusId, properties]);
  
  // Get user role from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = ['admin', 'super_admin', 'agent'].includes(user.role);
  
  // Modal states
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [galleryOpen, setGalleryOpen] = useState(false);
  const [galleryProperty, setGalleryProperty] = useState(null);
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  
  // Units management
  const [isUnitsOpen, setIsUnitsOpen] = useState(false);
  const [selectedPropertyForUnits, setSelectedPropertyForUnits] = useState(null);
  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [isUnitFormOpen, setIsUnitFormOpen] = useState(false);
  const [unitFormData, setUnitFormData] = useState(initialUnitData);
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [savingUnit, setSavingUnit] = useState(false);
  
  // Tenant assignment
  const [isAssignOpen, setIsAssignOpen] = useState(false);
  const [unitToAssign, setUnitToAssign] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  
  // Mandate termination
  const [isTerminateOpen, setIsTerminateOpen] = useState(false);
  const [propertyToTerminate, setPropertyToTerminate] = useState(null);
  const [terminationReason, setTerminationReason] = useState('');
  const [terminationNotes, setTerminationNotes] = useState('');
  const [terminating, setTerminating] = useState(false);

  // E-signature du mandat
  const [signatureModalOpen, setSignatureModalOpen] = useState(false);
  const [signatureLinkData, setSignatureLinkData] = useState(null);
  const [signatureLoading, setSignatureLoading] = useState(false);
  // Signature côté Mandataire (agence)
  const [agencySignaturePadOpen, setAgencySignaturePadOpen] = useState(false);
  const [agencySignerName, setAgencySignerName] = useState('');
  const [agencySigning, setAgencySigning] = useState(false);
  const [agencyDefaults, setAgencyDefaults] = useState(null);

  useEffect(() => {
    fetchProperties();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProperties = async () => {
    try {
      const response = await propertiesAPI.getAll();
      // Enrich with unit counts
      const enrichedProperties = await Promise.all(
        response.data.map(async (prop) => {
          if (prop.is_multi_unit) {
            try {
              const unitsRes = await propertiesAPI.getUnits(prop.id);
              const totalUnits = unitsRes.data.length;
              const rentedUnits = unitsRes.data.filter(u => u.status === 'loue').length;
              return { ...prop, totalUnits, rentedUnits };
            } catch {
              return { ...prop, totalUnits: 0, rentedUnits: 0 };
            }
          }
          return prop;
        })
      );
      setProperties(enrichedProperties);
    } catch (error) {
      toast.error('Erreur lors du chargement des biens');
    } finally {
      setLoading(false);
    }
  };

  const fetchUnits = async (propertyId) => {
    setLoadingUnits(true);
    try {
      const response = await propertiesAPI.getUnits(propertyId);
      setUnits(response.data);
    } catch (error) {
      toast.error('Erreur lors du chargement des unites');
    } finally {
      setLoadingUnits(false);
    }
  };

  const fetchTenants = async () => {
    try {
      const response = await clientsAPI.getAll({ client_type: 'locataire' });
      setTenants(response.data);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
      // Silent fail - tenants list not critical
    }
  };

  const handleOpenUnits = async (property) => {
    setSelectedPropertyForUnits(property);
    setIsUnitsOpen(true);
    await fetchUnits(property.id);
    await fetchTenants();
  };

  const handleOpenUnitForm = (unit = null) => {
    if (unit) {
      setSelectedUnit(unit);
      setUnitFormData({
        unit_name: unit.unit_name,
        unit_type: unit.unit_type,
        rent_amount: unit.rent_amount?.toString() || '',
        currency: unit.currency || 'XOF',
        surface: unit.surface?.toString() || '',
        rooms: unit.rooms?.toString() || '',
        description: unit.description || '',
        commission_rate: unit.commission_rate?.toString() || '',
      });
    } else {
      setSelectedUnit(null);
      setUnitFormData(initialUnitData);
    }
    setIsUnitFormOpen(true);
  };

  const handleSubmitUnit = async (e) => {
    e.preventDefault();
    if (!unitFormData.unit_name || !unitFormData.rent_amount) {
      toast.error('Veuillez remplir les champs obligatoires');
      return;
    }

    setSavingUnit(true);
    try {
      const submitData = {
        ...unitFormData,
        rent_amount: parseFloat(unitFormData.rent_amount),
        surface: unitFormData.surface ? parseFloat(unitFormData.surface) : null,
        rooms: unitFormData.rooms ? parseInt(unitFormData.rooms) : null,
        commission_rate: unitFormData.commission_rate ? parseFloat(unitFormData.commission_rate) : null,
      };

      if (selectedUnit) {
        await propertiesAPI.updateUnit(selectedPropertyForUnits.id, selectedUnit.id, submitData);
        toast.success('Unite mise a jour');
      } else {
        await propertiesAPI.createUnit(selectedPropertyForUnits.id, submitData);
        toast.success('Unite ajoutee');
      }
      
      setIsUnitFormOpen(false);
      setUnitFormData(initialUnitData);
      setSelectedUnit(null);
      await fetchUnits(selectedPropertyForUnits.id);
      await fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'enregistrement');
    } finally {
      setSavingUnit(false);
    }
  };

  const handleDeleteUnit = async (unitId) => {
    if (!window.confirm('Supprimer cette unite ?')) return;
    
    try {
      await propertiesAPI.deleteUnit(selectedPropertyForUnits.id, unitId);
      toast.success('Unite supprimee');
      await fetchUnits(selectedPropertyForUnits.id);
      await fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  const handleOpenAssign = (unit) => {
    setUnitToAssign(unit);
    setSelectedTenantId('');
    setIsAssignOpen(true);
  };

  const handleAssignTenant = async () => {
    if (!selectedTenantId) {
      toast.error('Veuillez selectionner un locataire');
      return;
    }

    try {
      await propertiesAPI.assignTenant(
        selectedPropertyForUnits.id,
        unitToAssign.id,
        selectedTenantId,
        new Date().toISOString()
      );
      toast.success('Locataire assigne avec succes');
      setIsAssignOpen(false);
      await fetchUnits(selectedPropertyForUnits.id);
      await fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'assignation');
    }
  };

  const handleReleaseUnit = async (unit) => {
    if (!window.confirm(`Liberer l'unite ${unit.unit_name} ?`)) return;
    
    try {
      await propertiesAPI.releaseUnit(selectedPropertyForUnits.id, unit.id);
      toast.success('Unite liberee');
      await fetchUnits(selectedPropertyForUnits.id);
      await fetchProperties();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la liberation');
    }
  };

  const handleOpenForm = (property = null) => {
    if (property) {
      setSelectedProperty(property);
      setFormData({
        ...property,
        price: property.price.toString(),
        surface: property.surface.toString(),
        bedrooms: property.bedrooms?.toString() || '',
        total_units: property.total_units?.toString() || '',
        bathrooms: property.bathrooms?.toString() || '',
        property_code: property.property_code || '',
        commission_rate: property.commission_rate ?? 5,
        owner_name: property.owner_name || '',
        owner_phone: property.owner_phone || '',
        owner_email: property.owner_email || '',
        owner_address: property.owner_address || '',
        owner_city: property.owner_city || '',
        owner_country: property.owner_country || 'Mali',
        owner_id_type: property.owner_id_type || '',
        owner_id_number: property.owner_id_number || '',
        owner_birth_date: property.owner_birth_date || '',
        owner_birth_place: property.owner_birth_place || '',
        owner_nationality: property.owner_nationality || '',
        owner_profession: property.owner_profession || '',
        owner_marital_status: property.owner_marital_status || '',
        mandate_type: property.mandate_type || 'exclusif',
        mandate_duration_months: property.mandate_duration_months ?? 12,
        mandate_security_deposit: property.mandate_security_deposit?.toString() || '',
        mandate_advance_months: property.mandate_advance_months?.toString() || '',
        mandate_file_fee: property.mandate_file_fee?.toString() || '',
        mandate_entry_commission_rate: property.mandate_entry_commission_rate?.toString() || '',
        mandate_monthly_management_fee_rate: property.mandate_monthly_management_fee_rate?.toString() || '',
        mandate_vat_rate: property.mandate_vat_rate?.toString() || '',
        mandate_notes: property.mandate_notes || '',
        mandate_start_date: property.mandate_start_date ? new Date(property.mandate_start_date) : new Date(),
      });
    } else {
      setSelectedProperty(null);
      setFormData(initialFormData);
    }
    setIsFormOpen(true);
  };

  const handleCloseForm = () => {
    setIsFormOpen(false);
    setSelectedProperty(null);
    setFormData(initialFormData);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.price || !formData.surface || !formData.address || !formData.district) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setSaving(true);
    try {
      const submitData = {
        ...formData,
        price: parseFloat(formData.price),
        surface: parseFloat(formData.surface),
        bedrooms: formData.bedrooms ? parseInt(formData.bedrooms) : null,
        bathrooms: formData.bathrooms ? parseInt(formData.bathrooms) : null,
        total_units: formData.total_units ? parseInt(formData.total_units) : null,
        property_code: formData.property_code?.trim() || null,
        commission_rate: formData.commission_rate ? parseFloat(formData.commission_rate) : 5.0,
        owner_name: formData.owner_name?.trim() || null,
        owner_phone: formData.owner_phone?.trim() || null,
        owner_email: formData.owner_email?.trim() || null,
        owner_address: formData.owner_address?.trim() || null,
        owner_city: formData.owner_city?.trim() || null,
        owner_country: formData.owner_country?.trim() || null,
        owner_id_type: formData.owner_id_type?.trim() || null,
        owner_id_number: formData.owner_id_number?.trim() || null,
        owner_birth_date: formData.owner_birth_date?.trim() || null,
        owner_birth_place: formData.owner_birth_place?.trim() || null,
        owner_nationality: formData.owner_nationality?.trim() || null,
        owner_profession: formData.owner_profession?.trim() || null,
        owner_marital_status: formData.owner_marital_status?.trim() || null,
        mandate_type: formData.mandate_type || null,
        mandate_duration_months: formData.mandate_duration_months ? parseInt(formData.mandate_duration_months) : null,
        mandate_security_deposit: formData.mandate_security_deposit ? parseFloat(formData.mandate_security_deposit) : null,
        mandate_advance_months: formData.mandate_advance_months ? parseInt(formData.mandate_advance_months) : null,
        mandate_file_fee: formData.mandate_file_fee ? parseFloat(formData.mandate_file_fee) : null,
        mandate_entry_commission_rate: formData.mandate_entry_commission_rate ? parseFloat(formData.mandate_entry_commission_rate) : null,
        mandate_monthly_management_fee_rate: formData.mandate_monthly_management_fee_rate ? parseFloat(formData.mandate_monthly_management_fee_rate) : null,
        mandate_vat_rate: formData.mandate_vat_rate ? parseFloat(formData.mandate_vat_rate) : null,
        mandate_notes: formData.mandate_notes?.trim() || null,
        mandate_start_date: formData.mandate_start_date ? formData.mandate_start_date.toISOString() : new Date().toISOString(),
      };

      if (selectedProperty) {
        await propertiesAPI.update(selectedProperty.id, submitData);
        toast.success('Bien mis à jour avec succès');
      } else {
        await propertiesAPI.create(submitData);
        toast.success('Bien ajouté avec succès');
      }
      
      handleCloseForm();
      fetchProperties();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de l\'enregistrement';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedProperty) return;
    
    try {
      await propertiesAPI.delete(selectedProperty.id);
      toast.success('Bien supprimé avec succès');
      setIsDeleteOpen(false);
      setSelectedProperty(null);
      fetchProperties();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleDownloadMandate = async (property) => {
    try {
      toast.info('Génération du mandat en cours...');
      const response = await propertiesAPI.downloadMandate(property.id);
      
      // Handle blob response errors
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Erreur lors du téléchargement');
      }
      
      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `mandat_courtage_${property.property_code || property.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Mandat de courtage téléchargé');
    } catch (error) {
      let message = 'Erreur lors du téléchargement';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message = errorData.detail || message;
        } catch (parseError) {
          console.error('Error parsing blob response:', parseError);
          // Unable to parse blob response
        }
      } else if (error.message) {
        message = error.message;
      }
      toast.error(message);
    }
  };

  const handleOpenSignatureLink = async (property) => {
    setSignatureLoading(true);
    setSignatureModalOpen(true);
    setSignatureLinkData({ property });
    setAgencySignaturePadOpen(false);
    try {
      const [linkRes, agencyRes] = await Promise.all([
        propertiesAPI.createSignatureLink(property.id),
        agencyDefaults ? Promise.resolve({ data: agencyDefaults }) : agencySettingsAPI.get().catch(() => ({ data: {} })),
      ]);
      const publicUrl = `${window.location.origin}${linkRes.data.public_path}`;
      if (agencyRes?.data) setAgencyDefaults(agencyRes.data);
      setAgencySignerName(property.mandate_agency_signer_name || agencyRes?.data?.legal_representative || '');
      setSignatureLinkData({
        property,
        ...linkRes.data,
        public_url: publicUrl,
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la génération du lien');
      setSignatureModalOpen(false);
    } finally {
      setSignatureLoading(false);
    }
  };

  const handleAgencySign = async (signatureB64) => {
    if (!signatureLinkData?.property?.id) return;
    if (!agencySignerName || agencySignerName.trim().length < 3) {
      toast.error('Veuillez saisir votre nom (min. 3 caractères)');
      return;
    }
    setAgencySigning(true);
    try {
      const { data } = await propertiesAPI.signMandateAsAgency(signatureLinkData.property.id, {
        signature_base64: signatureB64,
        signer_name: agencySignerName.trim(),
      });
      toast.success('Signature agence enregistrée');
      setAgencySignaturePadOpen(false);
      setSignatureLinkData((s) => ({
        ...s,
        property: {
          ...s.property,
          mandate_agency_signed_at: data.signed_at,
          mandate_agency_signer_name: data.signer_name,
          mandate_agency_signature_base64: signatureB64,
        },
      }));
      fetchProperties();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setAgencySigning(false);
    }
  };

  const handleClearAgencySignature = async () => {
    if (!signatureLinkData?.property?.id) return;
    if (!window.confirm('Retirer la signature du Mandataire ?')) return;
    try {
      await propertiesAPI.clearAgencySignature(signatureLinkData.property.id);
      toast.success('Signature Mandataire retirée');
      setSignatureLinkData((s) => ({
        ...s,
        property: {
          ...s.property,
          mandate_agency_signed_at: null,
          mandate_agency_signer_name: null,
          mandate_agency_signature_base64: null,
        },
      }));
      fetchProperties();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Erreur');
    }
  };

  const copySignatureLink = () => {
    if (!signatureLinkData?.public_url) return;
    navigator.clipboard.writeText(signatureLinkData.public_url);
    toast.success('Lien copié dans le presse-papier');
  };

  const shareSignatureWhatsApp = () => {
    if (!signatureLinkData?.public_url) return;
    const phone = (signatureLinkData.owner_phone || '').replace(/[^0-9]/g, '');
    const wa = phone.length >= 8 ? (phone.startsWith('223') ? phone : `223${phone}`) : '';
    const msg = encodeURIComponent(
      `Bonjour ${signatureLinkData.owner_name || ''},\n\nVeuillez signer électroniquement votre mandat de gestion en cliquant sur ce lien sécurisé :\n${signatureLinkData.public_url}\n\nLe lien est valable 30 jours. Merci pour votre confiance.`
    );
    const url = wa ? `https://wa.me/${wa}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(url, '_blank');
  };

  const shareSignatureEmail = () => {
    if (!signatureLinkData?.public_url) return;
    const to = signatureLinkData.owner_email || '';
    const subject = encodeURIComponent('Signature de votre mandat de gestion');
    const body = encodeURIComponent(
      `Bonjour ${signatureLinkData.owner_name || ''},\n\nVeuillez signer électroniquement votre mandat de gestion en cliquant sur ce lien sécurisé :\n${signatureLinkData.public_url}\n\nLe lien est valable 30 jours.\n\nMerci pour votre confiance.`
    );
    window.location.href = `mailto:${to}?subject=${subject}&body=${body}`;
  };

  const handleTerminateMandate = async () => {
    if (!propertyToTerminate || !terminationReason) {
      toast.error('Veuillez sélectionner un motif de fin de mandat');
      return;
    }
    
    setTerminating(true);
    try {
      await propertiesAPI.terminateMandate(propertyToTerminate.id, {
        end_reason: terminationReason,
        end_notes: terminationNotes || null
      });
      toast.success('Mandat terminé avec succès');
      setIsTerminateOpen(false);
      setPropertyToTerminate(null);
      setTerminationReason('');
      setTerminationNotes('');
      fetchProperties();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la terminaison du mandat';
      toast.error(message);
    } finally {
      setTerminating(false);
    }
  };

  const handleReactivateMandate = async (property) => {
    try {
      await propertiesAPI.reactivateMandate(property.id);
      toast.success('Mandat réactivé avec succès');
      fetchProperties();
    } catch (error) {
      const message = error.response?.data?.detail || 'Erreur lors de la réactivation';
      toast.error(message);
    }
  };

  const handleDownloadTerminationCertificate = async (property) => {
    try {
      toast.info('Génération de l\'attestation...');
      const response = await propertiesAPI.downloadTerminationCertificate(property.id);
      
      if (response.data.type === 'application/json') {
        const text = await response.data.text();
        const errorData = JSON.parse(text);
        throw new Error(errorData.detail || 'Erreur');
      }
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attestation_fin_mandat_${property.property_code || property.id.slice(0, 8)}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Attestation de fin de mandat téléchargée');
    } catch (error) {
      let message = 'Erreur lors du téléchargement';
      if (error.response?.data instanceof Blob) {
        try {
          const text = await error.response.data.text();
          const errorData = JSON.parse(text);
          message = errorData.detail || message;
        } catch (parseError) {
          console.error('Error parsing blob response:', parseError);
          // Unable to parse blob response
        }
      } else if (error.message) {
        message = error.message;
      }
      toast.error(message);
    }
  };

  const filteredProperties = properties.filter(property => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = property.title.toLowerCase().includes(searchLower) ||
      property.address.toLowerCase().includes(searchLower) ||
      property.district.toLowerCase().includes(searchLower) ||
      (property.property_code && property.property_code.toLowerCase().includes(searchLower));
    
    const matchesType = filterType === 'all' || property.property_type === filterType;
    const matchesStatus = filterStatus === 'all' || property.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="properties-page">
      <ModuleTasksPanel moduleKey="properties" moduleLabel="Biens" />
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900 tracking-tight">
            Biens Immobiliers
          </h1>
          <p className="text-gray-500 mt-1">
            {properties.length} bien{properties.length > 1 ? 's' : ''} enregistré{properties.length > 1 ? 's' : ''}
          </p>
        </div>
        <Button 
          onClick={() => handleOpenForm()}
          className="bg-[#1B3A4B] hover:bg-[#142D3A]"
          data-testid="add-property-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter un bien
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher par titre, adresse, quartier ou code (BIEN-...)..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-properties-input"
              />
            </div>
            <Select value={filterType} onValueChange={setFilterType}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-type-select">
                <SelectValue placeholder="Type de bien" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                <SelectItem value="terrain">Terrain</SelectItem>
                <SelectItem value="maison">Maison</SelectItem>
                <SelectItem value="appartement">Appartement</SelectItem>
                <SelectItem value="local_commercial">Local Commercial</SelectItem>
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-[180px]" data-testid="filter-status-select">
                <SelectValue placeholder="Statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les statuts</SelectItem>
                <SelectItem value="disponible">Disponible</SelectItem>
                <SelectItem value="reserve">Reserve</SelectItem>
                <SelectItem value="vendu">Vendu</SelectItem>
                <SelectItem value="loue">Loue</SelectItem>
                <SelectItem value="partiel">Partiellement loue</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Properties Grid */}
      {filteredProperties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProperties.map((property) => {
            const IconComponent = propertyTypeIcons[property.property_type] || Building2;
            
            return (
              <Card key={property.id} className="property-card overflow-hidden">
                <div className="h-48 bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center relative overflow-hidden">
                  {property.images && property.images.length > 0 ? (
                    <>
                      <img
                        src={property.images[0].startsWith('http') ? property.images[0] : `${process.env.REACT_APP_BACKEND_URL}${property.images[0]}`}
                        alt={property.title}
                        className="w-full h-full object-cover"
                      />
                      {property.images.length > 1 && (
                        <Badge className="absolute bottom-2 right-2 bg-black/60 text-white border-0 gap-1">
                          <ImageIcon className="w-3 h-3" /> {property.images.length}
                        </Badge>
                      )}
                    </>
                  ) : (
                    <IconComponent className="w-16 h-16 text-gray-300" />
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <span className="text-xs font-mono bg-[#1B3A4B]/10 text-[#1B3A4B] px-2 py-0.5 rounded font-semibold">
                        {property.property_code || `BIEN-${property.id?.slice(0,6).toUpperCase()}`}
                      </span>
                      <h3 className="font-semibold text-gray-900 line-clamp-1 mt-1">
                        {property.title}
                      </h3>
                    </div>
                    <div className="flex flex-col items-end gap-1">
                      <Badge className={`${getStatusColor(property.status)} text-xs flex-shrink-0`}>
                        {statusLabels[property.status]}
                      </Badge>
                      {property.mandate_status === 'termine' && (
                        <Badge className="bg-red-100 text-red-800 text-xs">
                          Mandat terminé
                        </Badge>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-1 text-sm text-gray-500 mb-3">
                    <MapPin className="w-3.5 h-3.5" />
                    <span className="truncate">{property.district}, {property.city}</span>
                  </div>
                  
                  <div className="flex items-center justify-between text-sm mb-3">
                    <span className="text-gray-500">
                      {propertyTypeLabels[property.property_type] || property.property_type} · {property.surface} m²
                    </span>
                    {property.bedrooms && (
                      <span className="text-gray-500">{property.bedrooms} ch.</span>
                    )}
                  </div>
                  
                  {/* Multi-unit indicator */}
                  {property.is_multi_unit && (
                    <div className="flex items-center gap-2 mb-3 p-2 bg-[#D4A373]/10 rounded-lg">
                      <Layers className="w-4 h-4 text-[#D4A373]" />
                      <span className="text-sm font-medium">
                        {property.rentedUnits || 0}/{property.totalUnits || 0} unites louees
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto h-7 text-[#1B3A4B]"
                        onClick={() => handleOpenUnits(property)}
                        data-testid={`manage-units-${property.id}`}
                      >
                        <Eye className="w-3 h-3 mr-1" />
                        Gerer
                      </Button>
                    </div>
                  )}
                  
                  <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                    <div>
                      <p className="text-lg font-semibold text-[#1B3A4B]">
                        {formatCurrency(property.price, property.currency)}
                      </p>
                      <p className="text-xs text-gray-500">
                        {transactionTypeLabels[property.transaction_type]}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      {property.property_type === 'immeuble' && !property.is_multi_unit && (
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => handleOpenUnits(property)}
                          title="Ajouter des unites"
                          className="text-[#D4A373]"
                          data-testid={`add-units-${property.id}`}
                        >
                          <Layers className="w-4 h-4" />
                        </Button>
                      )}
                      
                      {/* Mandate actions based on status */}
                      {property.mandate_status !== 'termine' ? (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadMandate(property)}
                            title="Télécharger le mandat de courtage"
                            className="text-[#1B3A4B] hover:text-[#142D3A] hover:bg-[#1B3A4B]/10"
                            data-testid={`download-mandate-${property.id}`}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenSignatureLink(property)}
                            title={property.mandate_signed_at ? 'Mandat déjà signé — regénérer un lien' : 'Envoyer pour signature électronique'}
                            className={property.mandate_signed_at ? "text-[#2B3A2C] hover:text-[#1e2a20] hover:bg-[#2B3A2C]/10" : "text-[#B85C38] hover:text-[#a04d2f] hover:bg-[#B85C38]/10"}
                            data-testid={`sign-mandate-${property.id}`}
                          >
                            <PenTool className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => {
                              setPropertyToTerminate(property);
                              setIsTerminateOpen(true);
                            }}
                            title="Mettre fin au mandat"
                            className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            data-testid={`terminate-mandate-${property.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        </>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownloadTerminationCertificate(property)}
                            title="Télécharger l'attestation de fin"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                            data-testid={`download-termination-${property.id}`}
                          >
                            <FileCheck className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleReactivateMandate(property)}
                            title="Réactiver le mandat"
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            data-testid={`reactivate-mandate-${property.id}`}
                          >
                            <RefreshCw className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                      
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => { setGalleryProperty(property); setGalleryOpen(true); }}
                        title="Galerie photos"
                        data-testid={`gallery-property-${property.id}`}
                      >
                        <ImageIcon className="w-4 h-4 text-[#1B3A4B]" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleOpenForm(property)}
                        data-testid={`edit-property-${property.id}`}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {isAdmin && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => {
                            setSelectedProperty(property);
                            setIsDeleteOpen(true);
                          }}
                          data-testid={`delete-property-${property.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card>
          <CardContent className="py-12">
            <div className="text-center">
              <Building2 className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">Aucun bien trouvé</h3>
              <p className="text-gray-500 mb-4">
                {searchQuery || filterType !== 'all' || filterStatus !== 'all'
                  ? 'Essayez de modifier vos filtres'
                  : 'Commencez par ajouter votre premier bien'}
              </p>
              {!searchQuery && filterType === 'all' && filterStatus === 'all' && (
                <Button 
                  onClick={() => handleOpenForm()}
                  className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Ajouter un bien
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Property Form Modal */}
      <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedProperty ? 'Modifier le bien' : 'Ajouter un bien'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="title">Titre *</Label>
                <Input
                  id="title"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Villa moderne à ACI 2000"
                  data-testid="property-title-input"
                />
              </div>
              
              <div>
                <Label htmlFor="property_code">Identifiant personnalisé</Label>
                <Input
                  id="property_code"
                  value={formData.property_code}
                  onChange={(e) => setFormData({ ...formData, property_code: e.target.value })}
                  placeholder={selectedProperty ? selectedProperty.property_code : "Ex: BIEN-001"}
                  data-testid="property-code-input"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Laissez vide pour générer automatiquement
                </p>
              </div>
              
              <div>
                <Label htmlFor="property_type">Type de bien *</Label>
                <Select
                  value={formData.property_type}
                  onValueChange={(value) => setFormData({ ...formData, property_type: value })}
                >
                  <SelectTrigger data-testid="property-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="terrain">Terrain</SelectItem>
                    <SelectItem value="maison">Maison</SelectItem>
                    <SelectItem value="appartement">Appartement</SelectItem>
                    <SelectItem value="local_commercial">Local Commercial</SelectItem>
                    <SelectItem value="immeuble">Immeuble (Multi-unites)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="transaction_type">Type de transaction *</Label>
                <Select
                  value={formData.transaction_type}
                  onValueChange={(value) => setFormData({ ...formData, transaction_type: value })}
                >
                  <SelectTrigger data-testid="property-transaction-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vente">Vente</SelectItem>
                    <SelectItem value="location">Location</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="price">Prix *</Label>
                <Input
                  id="price"
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                  placeholder="50000000"
                  data-testid="property-price-input"
                />
              </div>
              
              <div>
                <Label htmlFor="currency">Devise</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger data-testid="property-currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="XOF">FCFA (XOF)</SelectItem>
                    <SelectItem value="EUR">Euro (EUR)</SelectItem>
                    <SelectItem value="USD">Dollar (USD)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="surface">Surface (m²) *</Label>
                <Input
                  id="surface"
                  type="number"
                  value={formData.surface}
                  onChange={(e) => setFormData({ ...formData, surface: e.target.value })}
                  placeholder="200"
                  data-testid="property-surface-input"
                />
              </div>
              
              <div>
                <Label htmlFor="bedrooms">Chambres</Label>
                <Input
                  id="bedrooms"
                  type="number"
                  value={formData.bedrooms}
                  onChange={(e) => setFormData({ ...formData, bedrooms: e.target.value })}
                  placeholder="3"
                  data-testid="property-bedrooms-input"
                />
              </div>
              
              <div>
                <Label htmlFor="bathrooms">Salles de bain</Label>
                <Input
                  id="bathrooms"
                  type="number"
                  value={formData.bathrooms}
                  onChange={(e) => setFormData({ ...formData, bathrooms: e.target.value })}
                  placeholder="2"
                  data-testid="property-bathrooms-input"
                />
              </div>
              
              <div>
                <Label htmlFor="total_units" className="flex items-center gap-1">
                  Nombre d&apos;unités locatives
                  <span className="text-[10px] text-gray-500 font-normal">(pour immeubles / villas subdivisées)</span>
                </Label>
                <Input
                  id="total_units"
                  type="number"
                  min="1"
                  value={formData.total_units}
                  onChange={(e) => setFormData({ ...formData, total_units: e.target.value })}
                  placeholder="Ex: 6"
                  data-testid="property-total-units-input"
                />
                <p className="text-[10px] text-gray-500 mt-1">
                  Utilisé pour calculer le taux d&apos;occupation du propriétaire. Laissez vide pour 1 unité.
                </p>
              </div>
              
              <div>
                <Label htmlFor="district">Quartier *</Label>
                <Select
                  value={formData.district}
                  onValueChange={(value) => setFormData({ ...formData, district: value })}
                >
                  <SelectTrigger data-testid="property-district-select">
                    <SelectValue placeholder="Sélectionner un quartier" />
                  </SelectTrigger>
                  <SelectContent>
                    {bamakoDistricts.map((district) => (
                      <SelectItem key={district} value={district}>
                        {district}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="address">Adresse *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Rue 305, porte 112"
                  data-testid="property-address-input"
                />
              </div>
              
              <div>
                <Label htmlFor="status">Statut</Label>
                <Select
                  value={formData.status}
                  onValueChange={(value) => setFormData({ ...formData, status: value })}
                >
                  <SelectTrigger data-testid="property-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="disponible">Disponible</SelectItem>
                    <SelectItem value="reserve">Réservé</SelectItem>
                    <SelectItem value="vendu">Vendu</SelectItem>
                    <SelectItem value="loue">Loué</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="md:col-span-2">
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Description détaillée du bien..."
                  rows={3}
                  data-testid="property-description-input"
                />
              </div>
              
              {/* Section Mandat de Courtage */}
              <div className="md:col-span-2 border-t pt-4 mt-2">
                <h3 className="text-sm font-semibold text-[#1B3A4B] mb-3 flex items-center gap-2">
                  <FileText className="w-4 h-4" />
                  Informations pour le Mandat de Courtage
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="commission_rate" className="flex items-center gap-1">
                      <Percent className="w-3 h-3" />
                      Commission (%)
                    </Label>
                    <Input
                      id="commission_rate"
                      type="number"
                      step="0.5"
                      min="0"
                      max="100"
                      value={formData.commission_rate}
                      onChange={(e) => setFormData({ ...formData, commission_rate: e.target.value })}
                      placeholder="5"
                      data-testid="property-commission-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="owner_name">Nom du propriétaire</Label>
                    <Input
                      id="owner_name"
                      value={formData.owner_name}
                      onChange={(e) => setFormData({ ...formData, owner_name: e.target.value })}
                      placeholder="Nom complet du propriétaire/bailleur"
                      data-testid="property-owner-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_phone">Téléphone propriétaire</Label>
                    <Input
                      id="owner_phone"
                      value={formData.owner_phone}
                      onChange={(e) => setFormData({ ...formData, owner_phone: e.target.value })}
                      placeholder="+223 XX XX XX XX"
                      data-testid="property-owner-phone-input"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="owner_address">Adresse du propriétaire</Label>
                    <Input
                      id="owner_address"
                      value={formData.owner_address}
                      onChange={(e) => setFormData({ ...formData, owner_address: e.target.value })}
                      placeholder="Adresse complète du propriétaire"
                      data-testid="property-owner-address-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_email">E-mail propriétaire</Label>
                    <Input
                      id="owner_email"
                      type="email"
                      value={formData.owner_email}
                      onChange={(e) => setFormData({ ...formData, owner_email: e.target.value })}
                      placeholder="email@exemple.com"
                      data-testid="property-owner-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_birth_date">Date de naissance</Label>
                    <Input
                      id="owner_birth_date"
                      value={formData.owner_birth_date}
                      onChange={(e) => setFormData({ ...formData, owner_birth_date: e.target.value })}
                      placeholder="JJ/MM/AAAA"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_birth_place">Lieu de naissance</Label>
                    <Input
                      id="owner_birth_place"
                      value={formData.owner_birth_place}
                      onChange={(e) => setFormData({ ...formData, owner_birth_place: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_nationality">Nationalité</Label>
                    <Input
                      id="owner_nationality"
                      value={formData.owner_nationality}
                      onChange={(e) => setFormData({ ...formData, owner_nationality: e.target.value })}
                      placeholder="Malienne"
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_profession">Profession</Label>
                    <Input
                      id="owner_profession"
                      value={formData.owner_profession}
                      onChange={(e) => setFormData({ ...formData, owner_profession: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_marital_status">Situation matrimoniale</Label>
                    <Input
                      id="owner_marital_status"
                      value={formData.owner_marital_status}
                      onChange={(e) => setFormData({ ...formData, owner_marital_status: e.target.value })}
                      placeholder="Marié(e) / Célibataire / ..."
                    />
                  </div>
                  <div>
                    <Label htmlFor="owner_id_type">Type de pièce</Label>
                    <Input
                      id="owner_id_type"
                      value={formData.owner_id_type}
                      onChange={(e) => setFormData({ ...formData, owner_id_type: e.target.value })}
                      placeholder="CNI / Passeport / Permis"
                    />
                  </div>
                  <div className="md:col-span-2">
                    <Label htmlFor="owner_id_number">Numéro de la pièce</Label>
                    <Input
                      id="owner_id_number"
                      value={formData.owner_id_number}
                      onChange={(e) => setFormData({ ...formData, owner_id_number: e.target.value })}
                    />
                  </div>

                  {/* Sous-bloc : Mandat & montants */}
                  <div className="md:col-span-3 border-t pt-3 mt-2">
                    <h4 className="text-xs font-semibold uppercase tracking-wider text-[#B85C38] mb-3">Conditions du mandat & montants</h4>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label htmlFor="mandate_type">Type de mandat</Label>
                        <select
                          id="mandate_type"
                          value={formData.mandate_type}
                          onChange={(e) => setFormData({ ...formData, mandate_type: e.target.value })}
                          className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm"
                        >
                          <option value="exclusif">Exclusif</option>
                          <option value="non_exclusif">Non exclusif</option>
                          <option value="simple">Simple</option>
                        </select>
                      </div>
                      <div>
                        <Label htmlFor="mandate_duration_months">Durée (mois)</Label>
                        <Input
                          id="mandate_duration_months"
                          type="number"
                          min="1"
                          value={formData.mandate_duration_months}
                          onChange={(e) => setFormData({ ...formData, mandate_duration_months: e.target.value })}
                          placeholder="12"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mandate_security_deposit">Dépôt de garantie</Label>
                        <Input
                          id="mandate_security_deposit"
                          type="number"
                          min="0"
                          value={formData.mandate_security_deposit}
                          onChange={(e) => setFormData({ ...formData, mandate_security_deposit: e.target.value })}
                          placeholder="150000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mandate_advance_months">Avance (mois)</Label>
                        <Input
                          id="mandate_advance_months"
                          type="number"
                          min="0"
                          value={formData.mandate_advance_months}
                          onChange={(e) => setFormData({ ...formData, mandate_advance_months: e.target.value })}
                          placeholder="2"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mandate_file_fee">Frais de dossier</Label>
                        <Input
                          id="mandate_file_fee"
                          type="number"
                          min="0"
                          value={formData.mandate_file_fee}
                          onChange={(e) => setFormData({ ...formData, mandate_file_fee: e.target.value })}
                          placeholder="25000"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mandate_entry_commission_rate">Commission d&apos;entrée (%)</Label>
                        <Input
                          id="mandate_entry_commission_rate"
                          type="number"
                          step="0.5"
                          min="0"
                          value={formData.mandate_entry_commission_rate}
                          onChange={(e) => setFormData({ ...formData, mandate_entry_commission_rate: e.target.value })}
                          placeholder="10"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mandate_monthly_management_fee_rate">Frais gestion mensuels (%)</Label>
                        <Input
                          id="mandate_monthly_management_fee_rate"
                          type="number"
                          step="0.5"
                          min="0"
                          value={formData.mandate_monthly_management_fee_rate}
                          onChange={(e) => setFormData({ ...formData, mandate_monthly_management_fee_rate: e.target.value })}
                          placeholder="8"
                        />
                      </div>
                      <div>
                        <Label htmlFor="mandate_vat_rate">TVA (%)</Label>
                        <Input
                          id="mandate_vat_rate"
                          type="number"
                          step="0.5"
                          min="0"
                          value={formData.mandate_vat_rate}
                          onChange={(e) => setFormData({ ...formData, mandate_vat_rate: e.target.value })}
                          placeholder="18"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <Label htmlFor="mandate_notes">Notes complémentaires (facultatif)</Label>
                      <Textarea
                        id="mandate_notes"
                        rows={2}
                        value={formData.mandate_notes}
                        onChange={(e) => setFormData({ ...formData, mandate_notes: e.target.value })}
                        placeholder="Toute clause particulière ou observation à faire figurer sur le mandat."
                      />
                    </div>
                  </div>

                  <div>
                    <Label>Date de début du mandat</Label>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !formData.mandate_start_date && "text-muted-foreground"
                          )}
                          data-testid="mandate-start-date-btn"
                        >
                          <Calendar className="mr-2 h-4 w-4" />
                          {formData.mandate_start_date 
                            ? format(formData.mandate_start_date, "PPP", { locale: fr }) 
                            : "Sélectionner une date"}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0" align="start">
                        <CalendarComponent
                          mode="single"
                          selected={formData.mandate_start_date}
                          onSelect={(date) => setFormData({ ...formData, mandate_start_date: date || new Date() })}
                          initialFocus
                        />
                      </PopoverContent>
                    </Popover>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={handleCloseForm}>
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                disabled={saving}
                data-testid="property-submit-btn"
              >
                {saving ? 'Enregistrement...' : selectedProperty ? 'Mettre à jour' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={isDeleteOpen} onOpenChange={setIsDeleteOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer ce bien ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irreversible. Le bien "{selectedProperty?.title}" sera definitivement supprime.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-property-btn"
            >
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Units Management Modal */}
      <Dialog open={isUnitsOpen} onOpenChange={setIsUnitsOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-[#1B3A4B]" />
              Gestion des Unites - {selectedPropertyForUnits?.title}
            </DialogTitle>
          </DialogHeader>
          
          <Tabs defaultValue="units" className="space-y-4">
            <TabsList>
              <TabsTrigger value="units" data-testid="tab-units">
                <Layers className="h-4 w-4 mr-1" /> Unites
              </TabsTrigger>
              <TabsTrigger value="keys" data-testid="tab-keys">
                <KeyRound className="h-4 w-4 mr-1" /> Stock de cles
              </TabsTrigger>
            </TabsList>

            <TabsContent value="units" className="space-y-4">
            {/* Add Unit Button */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-gray-500">
                {units.length} unite(s) | {units.filter(u => u.status === 'loue').length} louee(s)
              </p>
              <Button
                onClick={() => handleOpenUnitForm()}
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                data-testid="add-unit-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Ajouter une unite
              </Button>
            </div>

            {/* Units Table */}
            {loadingUnits ? (
              <div className="flex justify-center py-8">
                <div className="spinner" />
              </div>
            ) : units.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Unite</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Loyer</TableHead>
                    <TableHead>Commission</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Locataire</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {units.map((unit) => (
                    <TableRow key={unit.id}>
                      <TableCell className="font-medium">{unit.unit_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline">{unitTypeLabels[unit.unit_type] || unit.unit_type}</Badge>
                      </TableCell>
                      <TableCell className="font-semibold text-[#1B3A4B]">
                        {formatCurrency(unit.rent_amount, unit.currency)}/mois
                      </TableCell>
                      <TableCell>
                        {unit.commission_rate != null ? `${unit.commission_rate}%` : <span className="text-gray-400">Bien ({selectedPropertyForUnits?.commission_rate}%)</span>}
                      </TableCell>
                      <TableCell>
                        <Badge className={unit.status === 'loue' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}>
                          {unit.status === 'loue' ? 'Loue' : 'Disponible'}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {unit.tenant_name ? (
                          <div className="flex items-center gap-1">
                            <Users className="w-3 h-3 text-gray-400" />
                            <span className="text-sm">{unit.tenant_name}</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-1">
                          {unit.status === 'disponible' ? (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleOpenAssign(unit)}
                              title="Assigner un locataire"
                              className="text-[#1B3A4B]"
                            >
                              <UserPlus className="w-4 h-4" />
                            </Button>
                          ) : (
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleReleaseUnit(unit)}
                              title="Liberer l'unite"
                              className="text-orange-600"
                            >
                              <UserMinus className="w-4 h-4" />
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleOpenUnitForm(unit)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteUnit(unit.id)}
                            disabled={unit.status === 'loue'}
                            className="text-red-600"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Layers className="w-12 h-12 mx-auto mb-2 text-gray-300" />
                <p>Aucune unite. Cliquez sur "Ajouter une unite" pour commencer.</p>
              </div>
            )}
            </TabsContent>

            <TabsContent value="keys">
              {selectedPropertyForUnits && (
                <KeysInventoryTab propertyId={selectedPropertyForUnits.id} />
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsUnitsOpen(false)}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Unit Form Modal */}
      <Dialog open={isUnitFormOpen} onOpenChange={setIsUnitFormOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {selectedUnit ? 'Modifier l\'unite' : 'Ajouter une unite'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmitUnit} className="space-y-4">
            <div>
              <Label htmlFor="unit_name">Nom de l'unite *</Label>
              <Input
                id="unit_name"
                value={unitFormData.unit_name}
                onChange={(e) => setUnitFormData({ ...unitFormData, unit_name: e.target.value })}
                placeholder="Ex: Appartement A1, Boutique 01"
                data-testid="unit-name-input"
              />
            </div>
            
            <div>
              <Label htmlFor="unit_type">Type d'unite *</Label>
              <Select
                value={unitFormData.unit_type}
                onValueChange={(value) => setUnitFormData({ ...unitFormData, unit_type: value })}
              >
                <SelectTrigger data-testid="unit-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="appartement">Appartement</SelectItem>
                  <SelectItem value="boutique">Boutique</SelectItem>
                  <SelectItem value="bureau">Bureau</SelectItem>
                  <SelectItem value="studio">Studio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="rent_amount">Loyer mensuel *</Label>
                <Input
                  id="rent_amount"
                  type="number"
                  value={unitFormData.rent_amount}
                  onChange={(e) => setUnitFormData({ ...unitFormData, rent_amount: e.target.value })}
                  placeholder="150000"
                  data-testid="unit-rent-input"
                />
              </div>
              <div>
                <Label htmlFor="currency">Devise</Label>
                <Select
                  value={unitFormData.currency}
                  onValueChange={(value) => setUnitFormData({ ...unitFormData, currency: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="XOF">FCFA (XOF)</SelectItem>
                    <SelectItem value="EUR">Euro (EUR)</SelectItem>
                    <SelectItem value="USD">Dollar (USD)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="unit_commission">Commission agence (%)</Label>
              <Input
                id="unit_commission"
                type="number"
                step="0.5"
                value={unitFormData.commission_rate}
                onChange={(e) => setUnitFormData({ ...unitFormData, commission_rate: e.target.value })}
                placeholder="Ex: 10 (vide = commission du bien)"
                data-testid="unit-commission-input"
              />
              <p className="text-xs text-gray-400 mt-1">Laissez vide pour utiliser la commission du bien parent</p>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="surface">Surface (m²)</Label>
                <Input
                  id="surface"
                  type="number"
                  value={unitFormData.surface}
                  onChange={(e) => setUnitFormData({ ...unitFormData, surface: e.target.value })}
                  placeholder="50"
                />
              </div>
              <div>
                <Label htmlFor="rooms">Nombre de pieces</Label>
                <Input
                  id="rooms"
                  type="number"
                  value={unitFormData.rooms}
                  onChange={(e) => setUnitFormData({ ...unitFormData, rooms: e.target.value })}
                  placeholder="3"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="unit_description">Description</Label>
              <Textarea
                id="unit_description"
                value={unitFormData.description}
                onChange={(e) => setUnitFormData({ ...unitFormData, description: e.target.value })}
                placeholder="Description de l'unite..."
                rows={2}
              />
            </div>
            
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setIsUnitFormOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-[#1B3A4B] hover:bg-[#142D3A]"
                disabled={savingUnit}
                data-testid="unit-submit-btn"
              >
                {savingUnit ? 'Enregistrement...' : selectedUnit ? 'Mettre a jour' : 'Ajouter'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Assign Tenant Modal */}
      <Dialog open={isAssignOpen} onOpenChange={setIsAssignOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Assigner un locataire</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-sm text-gray-600">Unite selectionnee:</p>
              <p className="font-semibold">{unitToAssign?.unit_name}</p>
              <p className="text-sm text-[#1B3A4B]">{formatCurrency(unitToAssign?.rent_amount || 0, unitToAssign?.currency || 'XOF')}/mois</p>
            </div>
            
            <div>
              <Label>Selectionner un locataire *</Label>
              <Select value={selectedTenantId} onValueChange={setSelectedTenantId}>
                <SelectTrigger data-testid="select-tenant-for-unit">
                  <SelectValue placeholder="Choisir un locataire..." />
                </SelectTrigger>
                <SelectContent>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant.id} value={tenant.id}>
                      {tenant.name} - {tenant.phone}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {tenants.length === 0 && (
                <p className="text-xs text-amber-600 mt-1">
                  Aucun locataire trouve. Creez d'abord un client de type "Locataire".
                </p>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsAssignOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleAssignTenant}
              className="bg-[#1B3A4B] hover:bg-[#142D3A]"
              disabled={!selectedTenantId}
              data-testid="confirm-assign-tenant-btn"
            >
              Assigner
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Mandate Termination Dialog */}
      <Dialog open={isTerminateOpen} onOpenChange={setIsTerminateOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle className="w-5 h-5" />
              Mettre fin au mandat
            </DialogTitle>
          </DialogHeader>
          
          {propertyToTerminate && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-600">Bien concerné:</p>
                <p className="font-semibold">{propertyToTerminate.title}</p>
                <p className="text-sm text-gray-500">{propertyToTerminate.property_code}</p>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="termination-reason">Motif de fin de mandat *</Label>
                <Select
                  value={terminationReason}
                  onValueChange={setTerminationReason}
                >
                  <SelectTrigger data-testid="termination-reason-select">
                    <SelectValue placeholder="Sélectionnez un motif" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vente_realisee">Vente réalisée avec succès</SelectItem>
                    <SelectItem value="resiliation_mandant">Résiliation par le mandant (propriétaire)</SelectItem>
                    <SelectItem value="resiliation_agence">Résiliation par l'agence</SelectItem>
                    <SelectItem value="expiration">Expiration du délai du mandat</SelectItem>
                    <SelectItem value="autre">Autre motif</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="termination-notes">Observations (optionnel)</Label>
                <Textarea
                  id="termination-notes"
                  value={terminationNotes}
                  onChange={(e) => setTerminationNotes(e.target.value)}
                  placeholder="Informations complémentaires..."
                  rows={3}
                  data-testid="termination-notes-input"
                />
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Le bien ne sera pas supprimé. Vous pourrez réactiver le mandat ultérieurement 
                  ou télécharger une attestation de fin de mandat.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setIsTerminateOpen(false);
                setPropertyToTerminate(null);
                setTerminationReason('');
                setTerminationNotes('');
              }}
            >
              Annuler
            </Button>
            <Button
              onClick={handleTerminateMandate}
              className="bg-red-600 hover:bg-red-700"
              disabled={terminating || !terminationReason}
              data-testid="confirm-terminate-mandate-btn"
            >
              {terminating ? 'Traitement...' : 'Mettre fin au mandat'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Property photo gallery */}
      <PropertyGalleryModal
        open={galleryOpen}
        onClose={() => { setGalleryOpen(false); setGalleryProperty(null); }}
        property={galleryProperty}
        onChange={(updated) => {
          setGalleryProperty(updated);
          // Refresh list to reflect new cover photo
          fetchProperties();
        }}
      />

      {/* Modal e-signature du mandat */}
      <Dialog open={signatureModalOpen} onOpenChange={setSignatureModalOpen}>
        <DialogContent className="max-w-lg" data-testid="signature-link-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-[#2B3A2C]">
              <PenTool className="w-5 h-5 text-[#B85C38]" />
              Signature électronique du mandat
            </DialogTitle>
          </DialogHeader>
          {signatureLoading ? (
            <div className="py-8 text-center text-slate-500">
              <RefreshCw className="w-6 h-6 mx-auto mb-2 animate-spin" />
              Génération du lien sécurisé…
            </div>
          ) : signatureLinkData?.public_url ? (
            <div className="space-y-4">
              <div className="text-sm text-slate-600">
                Un lien sécurisé a été généré pour le Mandant. Partagez-le via WhatsApp ou Email.
                Le lien est valable <b>30 jours</b>.
              </div>

              <div className="p-3 rounded-xl bg-[#FBF6EE] border border-[#E8D9C0] text-xs">
                <div className="text-slate-500 uppercase tracking-wide mb-1">Mandant</div>
                <div className="font-semibold text-[#2B3A2C]">{signatureLinkData.owner_name || '—'}</div>
                <div className="text-slate-600">
                  {signatureLinkData.owner_phone || 'Aucun téléphone'} · {signatureLinkData.owner_email || 'Aucun email'}
                </div>
              </div>

              <div className="flex items-center gap-2">
                <Input
                  readOnly
                  value={signatureLinkData.public_url}
                  className="flex-1 text-xs bg-slate-50"
                  data-testid="signature-link-input"
                />
                <Button
                  variant="outline"
                  size="icon"
                  onClick={copySignatureLink}
                  title="Copier le lien"
                  data-testid="signature-copy-btn"
                >
                  <Copy className="w-4 h-4" />
                </Button>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Button
                  onClick={shareSignatureWhatsApp}
                  className="bg-[#25D366] hover:bg-[#1fbe5b] text-white rounded-full"
                  data-testid="signature-whatsapp-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  WhatsApp
                </Button>
                <Button
                  onClick={shareSignatureEmail}
                  disabled={!signatureLinkData.owner_email}
                  className="bg-[#2B3A2C] hover:bg-[#1e2a20] text-white rounded-full disabled:opacity-50"
                  data-testid="signature-email-btn"
                >
                  <MailIcon className="w-4 h-4 mr-2" />
                  Email
                </Button>
              </div>

              {signatureLinkData.property?.mandate_signed_at && (
                <div className="p-3 rounded-xl bg-[#2B3A2C]/5 border border-[#2B3A2C]/20 text-xs text-[#2B3A2C]">
                  Le mandat a déjà été signé par <b>{signatureLinkData.property.mandate_signer_name || 'le Mandant'}</b>.
                  Générer un nouveau lien invaliderait l'ancien.
                </div>
              )}

              {/* Signature côté Mandataire (agence) */}
              <div className="pt-4 border-t border-[#E8D9C0]">
                <div className="flex items-center gap-2 mb-2">
                  <div className="w-6 h-6 rounded-full bg-[#2B3A2C] flex items-center justify-center">
                    <PenTool className="w-3 h-3 text-white" />
                  </div>
                  <h4 className="font-semibold text-[#2B3A2C] text-sm">Signature du Mandataire (Agence)</h4>
                </div>

                {signatureLinkData.property?.mandate_agency_signed_at ? (
                  <div className="p-3 rounded-xl bg-[#2B3A2C]/5 border border-[#2B3A2C]/20">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-500 uppercase tracking-wide">Signé par</div>
                        <div className="font-semibold text-[#2B3A2C]">{signatureLinkData.property.mandate_agency_signer_name}</div>
                        <div className="text-xs text-slate-600">
                          Le {new Date(signatureLinkData.property.mandate_agency_signed_at).toLocaleString('fr-FR')}
                        </div>
                      </div>
                      {signatureLinkData.property?.mandate_agency_signature_base64 && (
                        <img
                          src={signatureLinkData.property.mandate_agency_signature_base64}
                          alt="Signature agence"
                          className="max-h-14 border border-slate-200 rounded bg-white"
                        />
                      )}
                    </div>
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => { setAgencySignaturePadOpen(true); }}
                        className="border-[#2B3A2C] text-[#2B3A2C] hover:bg-[#2B3A2C] hover:text-white"
                        data-testid="agency-resign-btn"
                      >
                        Re-signer
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={handleClearAgencySignature}
                        className="border-red-300 text-red-600 hover:bg-red-50"
                        data-testid="agency-clear-signature-btn"
                      >
                        Retirer
                      </Button>
                    </div>
                  </div>
                ) : agencySignaturePadOpen ? (
                  <div className="p-3 rounded-xl bg-[#FBF6EE] border-2 border-dashed border-[#2B3A2C]/40 space-y-2">
                    <div>
                      <Label className="text-xs uppercase tracking-wide text-slate-500">Nom du signataire (Mandataire)</Label>
                      <Input
                        value={agencySignerName}
                        onChange={(e) => setAgencySignerName(e.target.value)}
                        placeholder="Nom du représentant légal"
                        className="text-sm mt-1"
                        data-testid="agency-signer-name"
                      />
                    </div>
                    <SignaturePad
                      width={480}
                      height={160}
                      onSave={handleAgencySign}
                      onCancel={() => setAgencySignaturePadOpen(false)}
                    />
                    {agencySigning && (
                      <div className="text-xs text-slate-500 text-center">
                        <RefreshCw className="w-3 h-3 inline animate-spin mr-1" /> Enregistrement…
                      </div>
                    )}
                  </div>
                ) : (
                  <Button
                    onClick={() => setAgencySignaturePadOpen(true)}
                    className="w-full bg-[#2B3A2C] hover:bg-[#1e2a20] text-white rounded-full"
                    data-testid="agency-sign-btn"
                  >
                    <PenTool className="w-4 h-4 mr-2" />
                    Signer côté Agence
                  </Button>
                )}
                <p className="text-xs text-slate-500 mt-2">
                  La signature du Mandataire apparaîtra à côté de celle du Mandant sur le PDF du mandat.
                </p>
              </div>
            </div>
          ) : null}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSignatureModalOpen(false)} data-testid="signature-close-btn">
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Properties;
